/**
 * Job Hunter — a candidate's CV library (like LinkedIn's saved resumes). Each
 * CV is stored in ImageKit under the company's folder, its text is what the
 * agents read, and the AI health review says how to make it stronger. Agents
 * pick a CV each; "default" is the one any agent without a pick uses.
 */
import { DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ClipboardPaste, Eye, FileText, FileUp, LoaderCircle, Pencil, RefreshCw, ScanText, Sparkles, Star, Trash2, TriangleAlert, UploadCloud,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { jobHunterService, type JhCv, type JhCvSync, type JhProfile } from '@/services/job-hunter.service';
import { syncSummary } from './candidate/ReadFromCv';
import { agentHex, agentSoft, fmtBytes, fmtDate, scoreTone } from '@/lib/job-hunter/format';
import { refreshAgents, refreshJobHunter } from '@/lib/job-hunter/store';
import { Field, Pill, ScoreRing, Toggle } from './ui';
import { CvDetail, type CvDetailTab } from './CvDetail';

const MAX = 8 * 1024 * 1024;

/** Tells the user what reading the CV changed in the profile. */
function announceSync(r: JhCvSync | null | undefined) {
  if (!r) return;
  if (r.error) toast(r.error, { icon: '⚠️', duration: 7000 });
  if (r.changed.length) toast.success(`Profile ${r.mode === 'replace' ? 'updated' : 'filled'} from the CV: ${syncSummary(r)} — check it on the Profile tab`, { duration: 7000 });
}
const okType = (f: File) => /\.(pdf|txt|md)$/i.test(f.name) || f.type === 'application/pdf' || f.type.startsWith('text/');

function SourceBadge({ cv }: { cv: JhCv }) {
  // A file whose text source is still "pasted" means nothing came out of the PDF (an edit would make it "edited").
  if (cv.file && (cv.textChars !== undefined ? cv.textChars === 0 : cv.textSource === 'pasted')) return <Pill tone="danger" icon={<TriangleAlert size={11} />} title="No text came out of this file — ATS software can't read it either. Paste the text or upload a PDF exported from Word / Google Docs.">Not ATS-readable</Pill>;
  if (cv.textSource === 'file') return <Pill tone="success">Text from PDF</Pill>;
  if (cv.textSource === 'edited') return <Pill tone="info">Text edited</Pill>;
  return <Pill tone="neutral">Pasted text</Pill>;
}

function CvCard({ cv, onOpen, onChanged, onProfileSynced, profileUid }: { cv: JhCv; profileUid: string; onOpen: (tab: CvDetailTab) => void; onChanged: () => void; onProfileSynced: () => void }) {
  const [renaming, setRenaming] = useState(false);
  const [label, setLabel] = useState(cv.label);
  const [busy, setBusy] = useState<string | null>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  useEffect(() => setLabel(cv.label), [cv.label]);

  const act = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onChanged(); } catch { /* toasted */ } finally { setBusy(null); }
  };
  const rename = () => {
    setRenaming(false);
    const v = label.trim();
    if (!v || v === cv.label) { setLabel(cv.label); return; }
    void act('rename', () => jobHunterService.updateCv(cv.cvUid, { label: v }), 'Renamed');
  };
  const replace = (f?: File) => {
    if (!f) return;
    if (!okType(f) || f.size > MAX) { toast.error('Upload a PDF or text file up to 8 MB'); return; }
    void act('replace', async () => {
      const r = await jobHunterService.uploadCv(profileUid, f, { cvUid: cv.cvUid });
      announceSync(r.profileSync);
      if (r.profileSync?.changed.length) onProfileSynced();
    }, cv.isDefault ? 'File replaced — the profile was re-read from it' : 'File replaced — run the health review again');
  };
  const readProfile = () => {
    if (!window.confirm(`Update the candidate's profile and truth file from “${cv.label}”? Name, contact, education, languages, experience, skills and links follow the CV; work rights, salary and saved answers stay.`)) return;
    void act('read', async () => {
      const r = await jobHunterService.syncProfileFromCv(cv.cvUid, 'replace');
      announceSync(r);
      onProfileSynced();
    }, 'Profile read from the CV');
  };
  const remove = () => {
    const used = cv.usedBy?.length ? ` ${cv.usedBy.length} agent${cv.usedBy.length === 1 ? '' : 's'} using it switch to the default CV.` : '';
    if (window.confirm(`Delete “${cv.label}”? The file is removed from ImageKit.${used}`)) void act('delete', () => jobHunterService.deleteCv(cv.cvUid), 'CV deleted');
  };
  const score = cv.health?.score;

  return (
    <article className="jh-card flex flex-col overflow-hidden">
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <button type="button" onClick={() => onOpen('file')} className={clsx('grid h-12 w-10 shrink-0 place-items-center rounded-lg border', cv.file ? 'border-danger/30 bg-danger-soft text-danger' : 'border-border bg-surface-alt text-text-muted')} title="Preview">
            <FileText size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {renaming ? (
                <input className="jh-input !h-8 !text-[13.5px] font-semibold" autoFocus value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} onBlur={rename} onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') { setLabel(cv.label); setRenaming(false); } }} />
              ) : (
                <>
                  <h3 className="truncate text-[14.5px] font-bold tracking-tight">{cv.label}</h3>
                  <button type="button" title="Rename" onClick={() => setRenaming(true)} className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-text-muted hover:bg-surface-alt hover:text-text-primary"><Pencil size={12} /></button>
                </>
              )}
            </div>
            <div className="mt-0.5 truncate text-[11.5px] text-text-muted">
              {cv.file ? `${cv.file.name} · ${cv.file.pages ? `${cv.file.pages} p · ` : ''}${fmtBytes(cv.file.size)}` : 'No file'} · {fmtDate(cv.createdAt)}
            </div>
          </div>
          <button type="button" title={cv.isDefault ? 'Default CV' : 'Make default'} disabled={cv.isDefault || !!busy}
            onClick={() => act('default', () => jobHunterService.updateCv(cv.cvUid, { isDefault: true }), `“${cv.label}” is now the default CV`)}
            className={clsx('grid h-8 w-8 shrink-0 place-items-center rounded-lg border transition', cv.isDefault ? 'border-warning/40 bg-warning-soft text-warning' : 'border-border text-text-muted hover:border-warning hover:text-warning')}>
            <Star size={15} fill={cv.isDefault ? 'currentColor' : 'none'} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5"><SourceBadge cv={cv} />{cv.isDefault && <Pill tone="warning">Default</Pill>}</div>

        <button type="button" onClick={() => onOpen('file')} className="flex items-center gap-3 rounded-xl border border-border p-3 text-left transition hover:border-primary">
          <ScoreRing score={score ?? null} size={46} stroke={5} />
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold" style={score != null ? { color: scoreTone(score).color } : undefined}>{score != null ? 'CV health' : 'Not reviewed yet'}</div>
            <p className="line-clamp-2 text-[12px] text-text-secondary">{cv.health?.verdict || (score != null ? 'Open to see the issues and fixes.' : 'Run the AI review: issues, fixes, rewrites and missing skills.')}</p>
          </div>
        </button>

        <div className="min-h-[22px]">
          {cv.usedBy?.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-semibold text-text-muted">Used by</span>
              {cv.usedBy.map((a) => <span key={a.agentUid} className="jh-chip !text-[10.5px]" style={{ background: agentSoft(a.color), color: agentHex(a.color) }}>{a.name}{!a.active && ' (paused)'}</span>)}
            </div>
          ) : <span className="text-[11px] text-text-muted">No agent uses this CV yet</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-t border-border bg-surface-alt/40 px-3 py-2">
        <Button size="sm" variant="ghost" iconLeft={<Eye size={13} />} onClick={() => onOpen('file')}>Preview</Button>
        <Button size="sm" variant="ghost" iconLeft={<Sparkles size={13} />} onClick={() => onOpen('file')}>{score != null ? 'Health review' : 'Analyze'}</Button>
        <span className="flex-1" />
        <input ref={replaceRef} type="file" accept=".pdf,.txt,.md,application/pdf,text/plain" className="hidden" onChange={(e) => { replace(e.target.files?.[0]); e.target.value = ''; }} />
        <button type="button" title="Update the candidate's profile from this CV" disabled={!!busy || cv.textChars === 0} onClick={readProfile} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-primary hover:text-primary disabled:opacity-50">{busy === 'read' ? <LoaderCircle size={14} className="animate-spin" /> : <ScanText size={14} />}</button>
        <button type="button" title={cv.isDefault ? 'Replace file — the profile is re-read from the new file' : 'Replace file (agents keep using this CV)'} disabled={!!busy} onClick={() => replaceRef.current?.click()} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-primary hover:text-primary disabled:opacity-50">{busy === 'replace' ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>
        <button type="button" title="Delete" disabled={!!busy} onClick={remove} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-muted hover:border-danger hover:text-danger disabled:opacity-50"><Trash2 size={14} /></button>
      </div>
    </article>
  );
}

function PasteModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (label: string, text: string) => Promise<void> }) {
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Paste a CV" widthClass="max-w-2xl">
      <div className="space-y-4">
        <Field label="Name"><input className="jh-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Backend CV" maxLength={80} /></Field>
        <Field label="CV text" hint="The whole CV — at least a few paragraphs. Agents score jobs against exactly this text."><textarea className="jh-input font-mono text-[12.5px]" rows={14} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={text.trim().length < 200} onClick={async () => { setBusy(true); try { await onSave(label.trim(), text); setLabel(''); setText(''); onClose(); } catch { /* toasted */ } finally { setBusy(false); } }}>Add CV</Button>
      </div>
    </Modal>
  );
}

export function CvLibrary({ profile, onProfileSynced }: { profile: JhProfile; onProfileSynced?: () => void }) {
  const profileUid = profile.profileUid;
  const [cvs, setCvs] = useState<JhCv[] | null>(null);
  const [label, setLabel] = useState('');
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [pasting, setPasting] = useState(false);
  const [detail, setDetail] = useState<{ uid: string; tab: CvDetailTab } | null>(null);
  const [readProfile, setReadProfile] = useState(true);
  const pickRef = useRef<HTMLInputElement>(null);
  const synced = useCallback((r: JhCvSync | null | undefined) => { announceSync(r); if (r?.changed.length) onProfileSynced?.(); }, [onProfileSynced]);

  const load = useCallback(async () => {
    try { setCvs(await jobHunterService.cvs(profileUid)); } catch { setCvs([]); }
  }, [profileUid]);
  useEffect(() => { setCvs(null); void load(); }, [load]);
  const changed = useCallback(() => { void load(); void refreshAgents(); void refreshJobHunter(); }, [load]);

  const upload = async (f?: File) => {
    if (!f) return;
    if (!okType(f)) { toast.error('Upload the CV as a PDF (or a plain-text file)'); return; }
    if (f.size > MAX) { toast.error('The CV must be 8 MB or smaller'); return; }
    setUploading(f.name);
    try {
      const r = await jobHunterService.uploadCv(profileUid, f, { label: label.trim() || undefined, syncProfile: readProfile ? undefined : 'none' });
      setLabel('');
      if (r.extractedChars > 0) toast.success(`“${r.cv.label}” added — ${r.extractedChars.toLocaleString()} characters read`);
      else toast.error('Saved, but no text came out of that file — it is probably scanned. Paste the text in the CV’s Text tab.', { duration: 8000 });
      synced(r.profileSync);
      changed();
      setDetail({ uid: r.cv.cvUid, tab: r.extractedChars > 0 ? 'file' : 'text' });
    } catch { /* toasted */ } finally { setUploading(null); }
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setOver(false); void upload(e.dataTransfer.files?.[0]); };

  return (
    <div className="space-y-5 @container">
      <div className={clsx('jh-drop p-5 sm:p-6', over && 'is-over')} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
        <div className="flex flex-col items-center gap-4 text-center @4xl:flex-row @4xl:text-left">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary-soft text-primary">{uploading ? <LoaderCircle size={24} className="animate-spin" /> : <UploadCloud size={24} />}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold">{uploading ? `Uploading ${uploading}…` : 'Drop a CV here, or choose a file'}</div>
            <p className="jh-muted mt-0.5">PDF or text, up to 8 MB. Saved to your media library (ImageKit), its text is read for the agents, and you get an AI health review. Keep one CV per kind of role — e.g. “Flutter CV” and “Backend CV” — and pick one per agent.</p>
          </div>
          <div className="flex w-full flex-col gap-2 @xl:w-auto @xl:flex-row @xl:items-end">
            <div className="min-w-0 @xl:w-56"><input className="jh-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Name (optional), e.g. Flutter CV" maxLength={80} aria-label="CV name" /></div>
            <input ref={pickRef} type="file" accept=".pdf,.txt,.md,application/pdf,text/plain" className="hidden" onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
            <Button disabled={!!uploading} iconLeft={<FileUp size={15} />} onClick={() => pickRef.current?.click()}>Choose file</Button>
            <Button variant="secondary" disabled={!!uploading} iconLeft={<ClipboardPaste size={15} />} onClick={() => setPasting(true)}>Paste text</Button>
          </div>
        </div>
        <label className="mt-4 flex items-center justify-center gap-2.5 border-t border-border pt-3 text-left text-[12.5px] text-text-secondary @4xl:justify-start">
          <Toggle checked={readProfile} onChange={setReadProfile} label="Fill the profile from this CV" />
          <span><b className="text-text-primary">Fill the candidate’s profile from this CV</b> — name, contact, education, languages, experience, skills and links go into empty fields; you can edit them after.</span>
        </label>
      </div>

      {cvs === null ? (
        <div className="grid gap-4 @2xl:grid-cols-2 @6xl:grid-cols-3">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-surface-alt" />)}</div>
      ) : cvs.length === 0 ? (
        <p className="jh-muted text-center">No CVs yet. Add the first one above — it becomes the default CV for every agent.</p>
      ) : (
        <div className="grid gap-4 @2xl:grid-cols-2 @6xl:grid-cols-3">
          {cvs.map((cv) => <CvCard key={cv.cvUid} cv={cv} profileUid={profileUid} onChanged={changed} onProfileSynced={() => onProfileSynced?.()} onOpen={(tab) => setDetail({ uid: cv.cvUid, tab })} />)}
        </div>
      )}

      <PasteModal open={pasting} onClose={() => setPasting(false)} onSave={async (l, text) => {
        const cv = await jobHunterService.pasteCv(profileUid, { label: l || undefined, text, syncProfile: readProfile ? undefined : 'none' });
        toast.success('CV added');
        synced(cv.profileSync);
        changed();
        setDetail({ uid: cv.cvUid, tab: 'text' });
      }} />
      <CvDetail cvUid={detail?.uid ?? null} profile={profile} initialTab={detail?.tab} onClose={() => setDetail(null)} onChanged={changed} />
    </div>
  );
}
